'use client';

import { useEffect, useRef, useState } from 'react';
import AnimateOnScroll from '@/components/AnimateOnScroll';
import GridSection from '@/components/site/GridSection';
import { WipTag } from '@/components/sections/kit';
import { EASE_OUT, NOT_REDUCED, gsap, playOnEnter, useGSAP } from '@/components/sections/kit/motion';
import {
  AREAS,
  ENTRIES,
  TYPE_META,
  formatIso,
  sashFor,
  type ChangelogEntry,
  type EntryType,
} from './data';

/**
 * The feed's timeline spine sits ON a real vertical grid line. The content
 * column starts half a cell inside the left rail (mid-column rule), so the
 * body grid's verticals cross the column at 0.5 / 1.5 / 2.5… cells from its
 * left edge — the spine at 1.5 cells is the second one, promoted from
 * hairline to line-2 and carrying the dots. The dates hang right-anchored
 * off the spine and overflow into the left margin (editorial margin notes);
 * the cards start one full cell to the right, their left edge coinciding
 * with the NEXT vertical at 2.5 cells — so a whole cell of open grid
 * separates dot and card. GridGlow's wash fades every other line behind
 * the cards; this one survives as the feed's spine.
 */
const SPINE_X = 'calc(var(--grid-cell,72px)*1.5)';
/** Dot y: date block top-22px + half the 16px date line == card pad 22px + half the type row. */
const DOT_Y = 30;

type TypeFilter = 'all' | EntryType;
type Row = { kind: 'year'; year: string } | { kind: 'entry'; entry: ChangelogEntry };

const TABS: { id: TypeFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'feature', label: 'Features' },
  { id: 'improvement', label: 'Improvements' },
  { id: 'fix', label: 'Fixes' },
];

const typeCount = (t: TypeFilter) =>
  t === 'all' ? ENTRIES.length : ENTRIES.filter((e) => e.type === t).length;

function Spine({ first, last, isYear }: { first: boolean; last: boolean; isYear: boolean }) {
  const top = first ? (isYear ? 12 : DOT_Y) : 0;
  return (
    <span
      aria-hidden
      className="absolute hidden w-px -translate-x-1/2 bg-line-2 md:block"
      style={{
        left: SPINE_X,
        top,
        height: last ? `${Math.max(DOT_Y - top, 0)}px` : `calc(100% - ${top}px)`,
      }}
    />
  );
}

function YearRow({ year, first, last }: { year: string; first: boolean; last: boolean }) {
  return (
    <div className="relative pb-[26px]">
      <Spine first={first} last={last} isYear />
      {/* Mobile: a plain small heading; md+: a chip riding the spine. */}
      <span className="font-jetbrains text-[9.5px] font-semibold tracking-[0.14em] text-dim md:hidden">
        {year}
      </span>
      <span
        className="absolute top-0 hidden -translate-x-1/2 rounded-full border border-line-2 bg-bg-warm px-[11px] py-1 font-jetbrains text-[9.5px] font-semibold tracking-[0.14em] text-dim md:block"
        style={{ left: SPINE_X }}
      >
        {year}
      </span>
      <span aria-hidden className="hidden h-[26px] md:block" />
    </div>
  );
}

function EntryCard({
  entry,
  onPickArea,
}: {
  entry: ChangelogEntry;
  onPickArea: (area: string) => void;
}) {
  const meta = TYPE_META[entry.type];
  const sash = sashFor(entry);

  return (
    <div className="rounded-ref-lg border border-line-2 bg-surface p-[22px] shadow-ref-lg transition-[border-color,box-shadow] duration-200 hover:border-line-3 hover:shadow-[0_18px_50px_-18px_#10102830] md:px-[26px]">
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
        <span className="font-jetbrains text-[10px] font-semibold text-dim md:hidden">
          {formatIso(entry.iso)}
        </span>
        <span className="inline-flex items-center gap-[7px]">
          <span
            aria-hidden
            className="h-[6px] w-[6px] shrink-0 rounded-full"
            style={{ background: meta.color }}
          />
          <span className="font-jetbrains text-[9.5px] font-semibold tracking-[0.13em] text-dim">
            {meta.label}
          </span>
        </span>
        <button
          type="button"
          onClick={() => onPickArea(entry.area)}
          title="Filter by this area"
          className="rounded border border-line-2 px-[9px] py-[3px] font-jetbrains text-[9.5px] tracking-[0.08em] text-muted transition-colors hover:border-brand-600/40 hover:text-brand-600"
        >
          {entry.area}
        </button>
        <a
          href={`#e-${entry.iso}`}
          aria-label={`Link to the entry from ${formatIso(entry.iso)}`}
          className="ml-auto font-jetbrains text-[11px] text-line-3 transition-colors hover:text-brand-600"
        >
          #
        </a>
      </div>

      <h2 className="mt-3 font-onest text-xl font-extrabold leading-[1.28] tracking-[-0.015em] text-ink">
        {entry.title}
      </h2>
      <p className="mt-[9px] text-[14.5px] leading-[1.65] text-pretty text-muted">{entry.body}</p>

      {sash && (
        <div
          data-sash
          className="mt-4 flex flex-wrap items-center gap-x-3.5 gap-y-2 rounded-ref border border-dashed border-[color-mix(in_srgb,var(--color-data-green)_38%,transparent)] bg-[color-mix(in_srgb,var(--color-data-green)_3.5%,transparent)] px-[15px] py-[11px]"
        >
          <span className="font-jetbrains text-[9px] tracking-[0.14em] text-dim">PROMISE KEPT</span>
          <span data-sash-before className="inline-flex">
            <WipTag label={sash.promisedLabel} variant="amber" />
          </span>
          <svg
            data-sash-arrow
            width="20"
            height="14"
            viewBox="0 0 24 16"
            fill="none"
            aria-hidden
            className="shrink-0"
          >
            <path
              d="M2 8h18m0 0-5-5m5 5-5 5"
              stroke="var(--color-data-green)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span data-sash-after className="inline-flex">
            <WipTag label={sash.shippedLabel} variant={sash.late ? 'amber' : 'green'} />
          </span>
          <span className="ml-auto font-jetbrains text-[9.5px] tracking-[0.1em] text-[color-mix(in_srgb,var(--color-data-green)_60%,var(--color-ink))]">
            {sash.note}
          </span>
        </div>
      )}
    </div>
  );
}

function EntryRow({
  entry,
  first,
  last,
  onPickArea,
}: {
  entry: ChangelogEntry;
  first: boolean;
  last: boolean;
  onPickArea: (area: string) => void;
}) {
  return (
    <div
      id={`e-${entry.iso}`}
      className="relative scroll-mt-[140px] pb-10 md:grid md:grid-cols-[calc(var(--grid-cell,72px)*1.5)_minmax(0,1fr)]"
    >
      {/* Right-anchored absolute block: wider dates overflow LEFT into the
          column margin instead of pushing into the spine. */}
      <div className="relative hidden md:block">
        <div className="absolute right-4 top-[22px] flex flex-col items-end gap-[5px] text-right">
          <span className="whitespace-nowrap font-jetbrains text-[11.5px] font-semibold leading-4 text-ink">
            {formatIso(entry.iso)}
          </span>
          <span className="whitespace-nowrap font-jetbrains text-[9.5px] tracking-[0.06em] text-dim">
            {entry.iso}
          </span>
        </div>
      </div>
      <Spine first={first} last={last} isYear={false} />
      <span
        aria-hidden
        className="absolute hidden h-[9px] w-[9px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-600 md:block"
        style={{
          left: SPINE_X,
          top: DOT_Y,
          boxShadow:
            '0 0 0 3px var(--color-bg-warm), 0 0 0 4px color-mix(in srgb, var(--color-brand-600) 28%, transparent)',
        }}
      />
      <div className="md:pl-[var(--grid-cell,72px)]">
        <AnimateOnScroll animation="fade-up">
          <EntryCard entry={entry} onPickArea={onPickArea} />
        </AnimateOnScroll>
      </div>
    </div>
  );
}

/**
 * Sticky filter bar + the release feed. Filters remount the feed subtree
 * (keyed by the filter state), so every card re-enters via AnimateOnScroll —
 * no bespoke re-filter animation, and reused DOM can never strand hidden.
 *
 * The sash-retirement choreography runs ONCE, for the sashes present at
 * mount: the amber promise tag dims, the arrow draws across, the green
 * shipped tag stamps in (the compare pages' rubber-stamp settle — pre-play
 * hidden state is opacity-only so a long tag can't add mobile overflow).
 * Sashes re-mounted later by a filter change get no timeline and simply
 * render finished — the resting DOM state is always the complete one.
 */
export default function ChangelogFeed() {
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [areaFilter, setAreaFilter] = useState<string>('all');
  const feedRef = useRef<HTMLDivElement>(null);
  // Zero-height anchor ABOVE the sticky bar: its document position is stable
  // (sticky elements report their stuck position, not their natural one).
  const topRef = useRef<HTMLDivElement>(null);
  const mounted = useRef(false);

  // Filtering a long feed from deep scroll can shrink the page and strand
  // the visitor in the footer — snap back to the top of the feed instead.
  // 'instant' on purpose: it's a layout correction, not a journey (and
  // smooth scrolling freezes in occluded/automated browsers).
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const anchor = topRef.current;
    if (!anchor) return;
    const top = anchor.getBoundingClientRect().top + window.scrollY;
    if (window.scrollY > top) window.scrollTo({ top: Math.max(top - 24, 0), behavior: 'instant' });
  }, [typeFilter, areaFilter]);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        const disposers: (() => void)[] = [];
        feedRef.current?.querySelectorAll<HTMLElement>('[data-sash]').forEach((sashEl) => {
          const before = sashEl.querySelector('[data-sash-before]');
          const arrow = sashEl.querySelector('[data-sash-arrow]');
          const after = sashEl.querySelector('[data-sash-after]');
          const tl = gsap.timeline({ paused: true });
          if (before) tl.to(before, { opacity: 0.55, duration: 0.5, ease: 'power2.out' }, 0.45);
          if (arrow) {
            gsap.set(arrow, { opacity: 0, x: -8 });
            tl.to(arrow, { opacity: 1, x: 0, duration: 0.4, ease: EASE_OUT }, 0.55);
          }
          if (after) {
            gsap.set(after, { opacity: 0 });
            tl.fromTo(
              after,
              { scale: 1.5, rotate: -8, opacity: 0 },
              {
                scale: 1,
                rotate: 0,
                opacity: 1,
                duration: 0.35,
                ease: 'power3.in',
                transformOrigin: '50% 50%',
                immediateRender: false,
              },
              0.8,
            );
          }
          const dispose = playOnEnter(tl, sashEl);
          if (dispose) disposers.push(dispose);
        });
        return () => disposers.forEach((d) => d());
      });
    },
    { scope: feedRef },
  );

  const visible = ENTRIES.filter(
    (e) => (typeFilter === 'all' || e.type === typeFilter) && (areaFilter === 'all' || e.area === areaFilter),
  );
  const isFiltered = typeFilter !== 'all' || areaFilter !== 'all';

  const rows: Row[] = [];
  let prevYear: string | null = null;
  visible.forEach((entry) => {
    const year = entry.iso.slice(0, 4);
    if (year !== prevYear) {
      rows.push({ kind: 'year', year });
      prevYear = year;
    }
    rows.push({ kind: 'entry', entry });
  });

  const pickArea = (area: string) => setAreaFilter(area);

  return (
    <GridSection className="pb-6 pt-14">
      <div ref={topRef} aria-hidden />
      {/* Left-aligned with the card column (2.5 cells in, past the spine) so
          the bar and the cards below share one edge. */}
      <div
        className="sticky top-[calc(var(--grid-cell,72px)+12px)] z-30 mb-[34px] flex flex-wrap items-center gap-x-3.5 gap-y-2.5 rounded-ref-lg border border-line-2 bg-surface px-3.5 py-2.5 shadow-[0_14px_40px_-14px_#10102826] md:ml-[calc(var(--grid-cell,72px)*2.5)]"
      >
        <div
          role="group"
          aria-label="Filter by type"
          className="inline-flex items-center gap-[2px] rounded-full border border-line-2 bg-bg-warm p-[3px]"
        >
          {TABS.map((tab) => {
            const active = typeFilter === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                aria-pressed={active}
                onClick={() => setTypeFilter(tab.id)}
                className={`inline-flex items-center gap-1.5 rounded-full border-none px-[13px] py-1.5 text-[12.5px] font-semibold transition-colors ${
                  active ? 'bg-ink text-white' : 'text-muted hover:text-ink'
                }`}
              >
                {tab.label}
                <span
                  className={`font-jetbrains text-[10px] ${active ? 'text-white/60' : 'text-dim'}`}
                >
                  {typeCount(tab.id)}
                </span>
              </button>
            );
          })}
        </div>
        <select
          aria-label="Filter by area"
          value={areaFilter}
          onChange={(ev) => setAreaFilter(ev.target.value)}
          className="cursor-pointer rounded-full border border-line-2 bg-bg-warm px-3 py-2 font-jetbrains text-[11px] tracking-[0.04em] text-ink outline-brand-600"
        >
          <option value="all">All areas</option>
          {AREAS.map((area) => (
            <option key={area} value={area}>
              {area}
            </option>
          ))}
        </select>
        <span className="ml-auto font-jetbrains text-[10px] tracking-[0.1em] text-dim">
          SHOWING {visible.length} / {ENTRIES.length}
        </span>
        {isFiltered && (
          <button
            type="button"
            onClick={() => {
              setTypeFilter('all');
              setAreaFilter('all');
            }}
            className="text-[12.5px] font-semibold text-brand-600 transition-colors hover:text-brand-700"
          >
            Clear &times;
          </button>
        )}
      </div>

      <div ref={feedRef}>
        {/* Keyed by the filter state: a filter change remounts the subtree so
            every visible card re-enters fresh. */}
        <div key={`${typeFilter}:${areaFilter}`}>
          {rows.map((row, i) =>
            row.kind === 'year' ? (
              <YearRow
                key={`y-${row.year}`}
                year={row.year}
                first={i === 0}
                last={i === rows.length - 1}
              />
            ) : (
              <EntryRow
                key={row.entry.iso}
                entry={row.entry}
                first={i === 0}
                last={i === rows.length - 1}
                onPickArea={pickArea}
              />
            ),
          )}

          {visible.length === 0 && (
            <div className="rounded-ref-lg border border-dashed border-line-3 p-9 text-center md:ml-[calc(var(--grid-cell,72px)*2.5)]">
              <p className="text-[14.5px] text-muted">Nothing shipped under that filter yet.</p>
              <button
                type="button"
                onClick={() => {
                  setTypeFilter('all');
                  setAreaFilter('all');
                }}
                className="mt-2.5 text-[13.5px] font-semibold text-brand-600 transition-colors hover:text-brand-700"
              >
                Clear filters
              </button>
            </div>
          )}

          {!isFiltered && (
            <p className="mt-1.5 font-jetbrains text-[10px] tracking-[0.12em] text-dim md:ml-[calc(var(--grid-cell,72px)*2.5)]">
              HISTORY STARTS FEB 2026 &mdash; THE API&rsquo;S GA. EARLIER INTERNAL HISTORY ISN&rsquo;T
              RESTATED HERE.
            </p>
          )}
        </div>
      </div>
    </GridSection>
  );
}
